import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Send, Loader2, Star } from 'lucide-react';

interface Message {
  role: string;
  content: string;
  timestamp: string;
}

interface ConversationHistory {
  session_id: string;
  patient_id: string;
  patient_name: string;
  conversation_history: Message[];
  model_name: string;
  created_at: string;
  updated_at: string;
}

const FeedbackProvidePage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [conversation, setConversation] = useState<ConversationHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [rating, setRating] = useState<number | null>(3);
  const [comments, setComments] = useState('');
  const [checklist, setChecklist] = useState({
    rapport: false,
    empathy: false,
    active_listening: false,
    open_questions: false,
    closed_questions: false,
    clarification: false,
    summarization: false,
    patient_education: false,
    cultural_sensitivity: false,
    confidentiality: false
  });

  const fetchConversationHistory = async () => {
    try {
      const response = await apiClient.get(`/api/conversation/history/${sessionId}`);
      setConversation(response.data);
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
      alert('Failed to load conversation history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversationHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleChecklistChange = (field: string, checked: boolean) => {
    setChecklist({
      ...checklist,
      [field]: checked
    });
  };

  const handleSubmitFeedback = async () => {
    if (!rating) {
      alert('Please provide a rating');
      return;
    }

    if (!comments.trim()) {
      alert('Please provide comments');
      return;
    }

    setSubmitting(true);

    try {
      await apiClient.post(`/api/feedback/submit`, {
        session_id: sessionId,
        rating: rating,
        comments: comments,
        checklist_items: checklist
      });

      alert('Feedback submitted successfully!');
      navigate('/patients');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user?.is_admin) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="mb-4">
              Only physicians and administrators can provide feedback on trainee sessions.
            </p>
            <Button onClick={() => navigate('/patients')}>Back to Patients</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/patients')}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-bold">Review and Provide Feedback</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {conversation && (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Conversation Transcript */}
            <Card className="flex-[2] max-h-[80vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>Conversation Transcript</CardTitle>
                <p className="text-sm text-muted-foreground">Patient: {conversation.patient_name}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {conversation.conversation_history.map((message, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-blue-100 dark:bg-blue-900'
                          : 'bg-gray-100 dark:bg-gray-800'
                      }`}
                    >
                      <p className="text-xs font-semibold text-muted-foreground mb-1">
                        {message.role === 'user' ? 'Trainee' : conversation.patient_name}
                      </p>
                      <p className="text-sm">{message.content}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Feedback Form */}
            <Card className="flex-1 max-h-[80vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>Physician Feedback</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Rating */}
                <div>
                  <h3 className="font-semibold mb-3">Overall Rating</h3>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        onClick={() => setRating(value)}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`h-8 w-8 ${
                            rating && value <= rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                    <span className="ml-2 text-sm font-medium">{rating}/5</span>
                  </div>
                </div>

                <Separator />

                {/* Checklist */}
                <div>
                  <h3 className="font-semibold mb-3">Skills Demonstrated</h3>
                  <div className="space-y-3">
                    {[
                      { id: 'rapport', label: 'Established rapport' },
                      { id: 'empathy', label: 'Demonstrated empathy' },
                      { id: 'active_listening', label: 'Active listening' },
                      { id: 'open_questions', label: 'Used open-ended questions' },
                      { id: 'closed_questions', label: 'Used closed questions appropriately' },
                      { id: 'clarification', label: 'Sought clarification when needed' },
                      { id: 'summarization', label: 'Summarized information' },
                      { id: 'patient_education', label: 'Provided patient education' },
                      { id: 'cultural_sensitivity', label: 'Cultural sensitivity' },
                      { id: 'confidentiality', label: 'Maintained confidentiality' }
                    ].map((item) => (
                      <div key={item.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={item.id}
                          checked={checklist[item.id as keyof typeof checklist]}
                          onCheckedChange={(checked) =>
                            handleChecklistChange(item.id, checked as boolean)
                          }
                        />
                        <Label htmlFor={item.id} className="text-sm cursor-pointer">
                          {item.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Comments */}
                <div>
                  <h3 className="font-semibold mb-3">Detailed Comments</h3>
                  <Textarea
                    rows={6}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Provide detailed feedback on the trainee's performance, including strengths and areas for improvement..."
                    className="resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleSubmitFeedback}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Feedback
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackProvidePage;
